import fetch from 'isomorphic-fetch';
import get from 'lodash.get';
import { getFormattedDateString } from 'lib/DateTime';

const DATE_OPTIONS = { format: 'd M D', useAP: false, commaAfterDay: false };

// 0: show all ads, 1: show only preroll, 2: show only midrolls, 3: show no ads
const adLevel = 0;

export const NEWS_NOW_PID = '2006328';

export const MSNBC_TVE_PID = '2007680';

export const TODAY_ALL_DAY_PID = '2004490';

export const NEWS_NOW_DEFAULT_SLATE = 'https://media1.s-nbcnews.com/i/newscms/2020_01/3168931/video-slate-fallback_363c83282682fdd7f06de8310326584d.png';

export const TODAY_ALL_DAY_DEFAULT_SLATE = 'https://media13.s-nbcnews.com/i/MSNBC/Components/Video/202007/TAD_GN_SERIES_IMAGE.jpg';

export function getPIDforPlayerKey(key) {
  const map = {
    NEWS_NOW: NEWS_NOW_PID,
    MSNBC_TVE: MSNBC_TVE_PID,
    TODAY_ALL_DAY: TODAY_ALL_DAY_PID,
  };
  if (map[key]) {
    return parseInt(map[key], 10);
  }
  return null;
}

export async function getPlaymakerById(pid) {
  return fetch(`https://api.leap.nbcsports.com/pid/NBCNews/${pid}/v4/desktop`)
    .then((res) => res.json())
    .then(({ pid: pidString, eventStatus: status, ...rest }) => ({
      ...rest,
      status,
      pid: pidString ? parseInt(pidString, 10) : null,
      isLive: status && status.toLowerCase() === 'live',
    }));
}

export async function getNewsNowPlaymaker() {
  return getPlaymakerById(NEWS_NOW_PID);
}

const playmakerConfiguration = {
  [NEWS_NOW_PID]: {
    cdnTokenEndpoint: 'https://tokens.playmakerservices.com',
    leapConfigUrl: 'https://api.leap.nbcsports.com/pid/NBCNews/{PID}/v4/{PLATFORM}',
  },
  [MSNBC_TVE_PID]: {
    leapConfigUrl: 'https://api.leap.nbcsports.com/pid/MSNBC/{PID}/v4/{PLATFORM}',
    // Support client side ad insertion using in-manifest SCTE tag parsing
    parseScte: 1,
  },
  [TODAY_ALL_DAY_PID]: {
    afid: '137705821',
    cdnTokenEndpoint: 'https://tokens.playmakerservices.com',
    csid: 'today_desktop_computer_web_livelinear',
    ignoredSCTEAdMessageTypes: ['breakStart', 'breakEnd'],
    leapConfigUrl: 'https://api.leap.nbcsports.com/pid/Today/{PID}/v4/{PLATFORM}',
    metr: '1023',
    poster: 'https://sprtsecureassets.akamaized.net/operations/slates/tad_huge_logo.jpg',
    resource: '',
    sfid: '7006068',
  },
};

export function getPlaymakerConfiguration(pid) {
  const config = get(playmakerConfiguration, pid, false);
  if (!config) {
    return playmakerConfiguration[NEWS_NOW_PID];
  }
  return config;
}

export function getAdLevelForPID(pid, envvars = {}) {
  switch (pid) {
    case MSNBC_TVE_PID:
      return get(envvars, 'PLAYMAKER_ADLEVEL_MSNBC_TVE', adLevel);

    case NEWS_NOW_PID:
      return get(envvars, 'PLAYMAKER_ADLEVEL_NEWS_NOW', adLevel);

    case TODAY_ALL_DAY_PID:
      return get(envvars, 'PLAYMAKER_ADLEVEL_TODAY_ALL_DAY', adLevel);
    default:
      return adLevel;
  }
}

export const getDefaultMetadata = (key) => {
  const shared = {
    playmakerArt: null,
    playmakerAutoplayMuted: true,
    playmakerPlayBehavior: 'clickToPlay',
    playmakerDescription: null,
    playmakerRenderMobileCover: true,
  };

  switch (key) {
    case MSNBC_TVE_PID:
    case 'msnbc':
      return {
        ...shared,
        playmakerId: MSNBC_TVE_PID,
        // Override in curation or use schedule data
        playmakerTitle: false,
        // Do not display on cover
        playmakerRenderMobileCover: false,
      };
    case TODAY_ALL_DAY_PID:
    case 'today':
      return {
        ...shared,
        playmakerId: TODAY_ALL_DAY_PID,
        playmakerTitle: 'Today All Day',
        playmakerDescription: 'Watch TODAY All Day. A streaming channel from TODAY.',
        playmakerArt: 'today_all_day_tease',
      };
    default:
      return {
        ...shared,
        playmakerId: NEWS_NOW_PID,
        playmakerTitle: `NBC News NOW for ${getFormattedDateString(Date.now(), null, DATE_OPTIONS)}`,
        playmakerDescription: 'NBC News NOW is a streaming network from NBC News. NOW features deep reporting, thoughtful storytelling, and live, breaking news from the global resources of NBC News.',
        playmakerArt: 'news-now-opening',
      };
  }
};

const mergeOverride = (defaultValue, override) => (
  defaultValue === 'other'
    ? override
    : defaultValue
);

export function getPlaymakerMetadata(metadata, vertical = 'nbcnews') {
  const { playmakerId: metaPID } = metadata;

  // Get default configuration based on playmakerId,
  // fall back to default configuration for vertical:
  const metaDefaults = getDefaultMetadata(metaPID || vertical);

  const {
    playmakerArt,
    playmakerArtOverride,
    playmakerId,
    playmakerIdOverride,
    playmakerToggle,
    ...rest
  } = {
    ...metaDefaults,
    ...metadata,
  };

  // Playmaker is turned OFF
  if (playmakerToggle !== true) return { playmakerToggle };

  let { playmakerSlot } = rest;
  if (typeof playmakerSlot !== 'undefined') {
    playmakerSlot = parseInt(playmakerSlot, 10);
  }

  // Playmaker is turned ON
  return {
    ...rest,
    playmakerSlot,
    playmakerToggle,
    // There are two inputs for Playmaker ID (a select and a textbox for injecting new pids)
    playmakerId: mergeOverride(playmakerId, playmakerIdOverride),
    // There are two inputs for Playmaker Art (a select and a textbox for injecting new guids)
    playmakerArt: mergeOverride(playmakerArt, playmakerArtOverride),
  };
}

export function isAutoplay(metadata) {
  return getPlaymakerMetadata(metadata).playmakerPlayBehavior === 'autoplay';
}
